import { ToastType } from '@components/Toast';
import {
  prepareUserDetailsChangeSummary,
  setUserDetailsChangeBaseline,
} from '@changeTracking/pages/userDetails/formChangeTracking';
import { fetchPostingGroupApi } from '@store/postingGroup/services';
import { userStore } from '@store/user/store';
import { handleToast, isAdmin } from '@utils/utils';
import { SwitchboxOption } from '@views/userDetails/components/PermissionsCard';
import {
  userPermissions,
  userAdjustmentTypes,
} from '@views/userDetails/constants';
import { produce } from 'solid-js/store';

import {
  fetchPastChargesApi,
  fetchPendingChargesApi,
  fetchUserDetailDocumentsApi,
  getATGUserDataApi,
  getUserDetailsApi,
  saveUserAdjustmentsApi,
  saveUserDetailsApi,
  storeGoogleCredentialApi,
} from './services';
import {
  resetUserDetailStore,
  setPermissionsUpdateTrigger,
  setRolesUpdateTrigger,
  setUserDetailStore,
  userDetailStore,
} from './store';
import { CheckboxOptionType, RoleViewModel, UserDetailsType } from './types';

const isThisUser = (id?: number) => {
  if (Boolean(id)) {
    const currentUser = userStore.user;
    return id == currentUser.id;
  }
  return true;
};

export const lockRoles = (roleName: string, userRoles: string[]): boolean => {
  if (
    roleName === 'Carrier Relations Admin' &&
    !userRoles.includes('Carrier Relations Admin')
  ) {
    return true;
  }
  const hasPrivilegedRole = ['Admin', 'Manager', 'Recruiter'].some((privRole) =>
    userRoles.includes(privRole),
  );
  return !hasPrivilegedRole;
};

export const lockPermissions = (
  permissionName: string,
  userId?: number,
): boolean => {
  const roles = getRelevantRoles(userId);
  const permissions = getRelevantPermissions(userId);
  switch (permissionName) {
    case 'Edit Carrier Contacts':
      return roles.includes('Carrier Relations');
    default:
      return permissions.includes('Edit Permissions');
  }
};

export const getRelevantRoles = (id?: number): string[] => {
  const isCurrentUser = isThisUser(id);
  return isCurrentUser
    ? (userDetailStore.userDetails.roles ?? [])
    : (userStore.user.roles ?? []);
};

export const getRelevantPermissions = (id?: number): string[] => {
  const isCurrentUser = isThisUser(id);
  return isCurrentUser
    ? (userDetailStore.userDetails.permissions ?? [])
    : (userStore.user.permissions ?? []);
};

export const mapRoleOptions = (
  availableRoles: RoleViewModel[] = [],
  userRoles: string[],
): CheckboxOptionType[] =>
  availableRoles.map(({ name = '', description }) => ({
    label: name,
    value: name,
    disabled: lockRoles(name, userRoles),
    description,
  }));

export const mapPermissionOptions = (
  availablePermissions: SwitchboxOption[] = [],
  id: number,
): SwitchboxOption[] =>
  availablePermissions.map((item) => ({
    label: item.label,
    value: item.value,
    disabled: !lockPermissions(item.value, id),
  }));

// ts-unused-exports:disable-next-line
export const hasPermission = (permission: string) => {
  return (
    userDetailStore.userDetails.permissions &&
    userDetailStore.userDetails.permissions.length > 0 &&
    userDetailStore.userDetails.permissions.includes(permission)
  );
};

const checkGoogleAuth = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.get('code');
};

const hasHandledGoogleAuth = (): boolean => {
  return sessionStorage.getItem('googleAuthHandled') === 'true';
};

const setGoogleAuthHandled = () => {
  sessionStorage.setItem('googleAuthHandled', 'true');
};

export const getUserDetailsAction = async (id?: number) => {
  setUserDetailStore('isLoading', true);
  if (id !== undefined && id !== userStore.user.id) {
    resetUserDetailStore();
  }
  await userStore.userPromise;

  const userId = id ?? userStore.user.id;
  const rawData = await getUserDetailsApi(userId);
  if (!rawData) return;

  await Promise.all([
    fetchUserPostingGroupAction(rawData.officeId),
    fetchUserDetailDocumentsAction(String(rawData.id)),
    fetchPendingChargesAction(String(rawData.id)),
    fetchPastChargesAction(String(rawData.id)),
  ]);

  const authCode = checkGoogleAuth();
  const shouldSaveGoogleCredentials =
    authCode != null && !hasHandledGoogleAuth() && !rawData.thirdPartyLinked;

  const googleLinked = shouldSaveGoogleCredentials
    ? await storeGoogleCredentialApi({
        userId: rawData.id,
        key: rawData.email,
        code: authCode,
        redirectUri: window.location.origin + '/user/details',
      })
    : false;

  if (googleLinked ?? false) {
    setGoogleAuthHandled();
    handleToast(ToastType.Success, 'AtgFr8.com successfully linked');
  }

  const thirdPartyLinked = (googleLinked ?? false) || rawData.thirdPartyLinked;
  const userDetails = {
    ...rawData,
    thirdPartyLinked,
  };

  const roles = isThisUser(rawData.id) ? rawData.roles : userStore.user.roles; // these roles only used for enabling or disabling.
  const roleOptions = mapRoleOptions(rawData.availableRoles, roles ?? []);

  setUserDetailStore(
    produce((draft) => {
      draft.userDetails = userDetails;
      draft.currentUser = isThisUser(id);
      draft.isLoading = false;
      draft.roles = roleOptions;
    }),
  );
  setUserDetailsChangeBaseline(userDetails);
};

const fetchUserDetailDocumentsAction = async (id: string) => {
  const data = await fetchUserDetailDocumentsApi(id);
  if (data) {
    setUserDetailStore('documents', data);
  }
};

export const fetchUserPostingGroupAction = async (id: number) => {
  const data = await fetchPostingGroupApi(String(id));
  if (data) {
    setUserDetailStore('postingGroup', data);
  }
};

export const getATGUserDataActionV4 = (id?: number) => {
  let availablePermissions;
  if (isAdmin()) {
    availablePermissions = Boolean(hasPermission('IT'))
      ? userPermissions.it
      : userPermissions.all;
  } else {
    availablePermissions = userPermissions.agent;
  }
  const userId = id ?? userStore.user.id;
  const formattedPermissions = availablePermissions.map((item) => ({
    label: item,
    value: item,
  }));
  const mappedPermissions = mapPermissionOptions(formattedPermissions, userId);
  const formattedPendingCharges = userAdjustmentTypes.map((item) => ({
    label: item,
    value: item,
  }));
  setUserDetailStore(
    produce((draft) => {
      draft.pendingChargeTypes = formattedPendingCharges;
      draft.permissions = mappedPermissions;
    }),
  );
};

export const getATGUserDataAction = async (id?: number) => {
  const data = await getATGUserDataApi();
  if (data) {
    let availablePermissions;
    if (isAdmin()) {
      availablePermissions = Boolean(hasPermission('IT'))
        ? data.user.permissions.iT
        : data.user.permissions.all;
    } else {
      availablePermissions = data.user.permissions.agent;
    }
    const userId = id ?? userStore.user.id;
    const formattedPermissions = availablePermissions.map((item) => ({
      label: item,
      value: item,
    }));
    const mappedPermissions = mapPermissionOptions(
      formattedPermissions,
      userId,
    );
    const formattedPendingCharges = data.user.adjustmentTypes.map((item) => ({
      label: item,
      value: item,
    }));
    setUserDetailStore(
      produce((draft) => {
        draft.pendingChargeTypes = formattedPendingCharges;
        draft.permissions = mappedPermissions;
      }),
    );
  }
};

const fetchPendingChargesAction = async (id: string) => {
  const data = await fetchPendingChargesApi(String(id));
  if (data) {
    setUserDetailStore('pendingCharges', data);
  }
};

export const fetchPastChargesAction = async (id: string) => {
  const data = await fetchPastChargesApi(id);
  if (data) {
    setUserDetailStore('pastCharges', data);
  }
};

export const saveUserAdjustmentsAction = async (
  userId: number,
  adjustment: Record<string, unknown>[],
) => {
  try {
    setUserDetailStore('loadingPendingCharges', true);
    const response = await saveUserAdjustmentsApi(userId, adjustment);
    if (response && response.isSuccess) {
      setUserDetailStore('pendingCharges', response.value);
      await fetchPendingChargesAction(userId.toString());
      handleToast(ToastType.Success, 'Charges and Fees Saved');
    }
  } finally {
    setUserDetailStore('loadingPendingCharges', false);
  }
};

export const saveUserDetailsAction = async (payload: UserDetailsType) => {
  const changedForms = prepareUserDetailsChangeSummary(payload).changedForms;
  const status = await saveUserDetailsApi(payload, changedForms);
  if (Boolean(status)) {
    setUserDetailStore('userDetails', payload);
    setUserDetailsChangeBaseline(payload);
    setRolesUpdateTrigger(true);
    setPermissionsUpdateTrigger(true);
    handleToast(ToastType.Success, 'User information saved');
  }
};
